import {otsBaseApi} from "../../ots-api";
import {
  ServiceEvent,
  ServiceEventBadgeSummary,
  ServiceEventCreateDTO,
  ServiceEventLocationFacet,
  ServiceEventSearchCriteria
} from "../../../model/service/service-event";
import {removeUndefined} from "../../../util/util";
import {HeatmapGrid, HeatmapGridRequest} from "../../../model/heatmap";

const ALL_TAG_TYPES = [
  "ServiceEvents",
  "ServiceEventLocationFacets",
  "ObjectObjectChangeLogs",
] as const;

type TagType = typeof ALL_TAG_TYPES[number];

const taggedBaseApi = otsBaseApi.enhanceEndpoints({
  addTagTypes: ALL_TAG_TYPES
});

const serviceEventApi = taggedBaseApi.injectEndpoints({
  endpoints: (builder) => ({
    searchServiceEvents: builder.query<
      ServiceEvent[],
      ServiceEventSearchCriteria
    >({
      query: (criteria) => ({
        url: `/service-events/search`,
        method: 'POST',
        body: criteria,
      }),
      providesTags: (result) => {
        if (result) {
          return [
            ...result.map(({id}) => ({type: "ServiceEvents", id} as const)),
            {type: "ServiceEvents", id: "LIST"}
          ];
        } else {
          return [{type: "ServiceEvents", id: "LIST"}];
        }
      },
    }),
    getServiceEvent: builder.query<ServiceEvent, string>({
      query: (id) => ({
        url: `/service-events/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "ServiceEvents", id}]
    }),
    getServiceEventBadgeSummary: builder.query<ServiceEventBadgeSummary, string>({
      query: (arg) => ({
        url: `/service-events/badge-summary`,
        method: "GET",
        params: removeUndefined({
          regionIds: [arg],
        }),
      }),
    }),
    getServiceEventLocationFacets: builder.query<ServiceEventLocationFacet[], string>({
      query: (id) => ({
        url: `/service-events/${id}/location-facets`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{type: "ServiceEventLocationFacets", id}]
    }),
    getServiceEventHeatmapGrid: builder.query<HeatmapGrid, { id: string, req: HeatmapGridRequest }>({
      query: ({id, req}) => ({
        url: `/service-events/${id}/heatmap/grid`,
        method: "POST",
        body: req
      }),
    }),
    createServiceEvent: builder.mutation<ServiceEvent, Partial<ServiceEvent>>({
      query: (event) => ({
        url: "/service-events",
        method: "POST",
        body: event
      }),
      invalidatesTags: [{type: "ServiceEvents", id: "LIST"}]
    }),
    updateServiceEvent: builder.mutation<ServiceEvent, Partial<ServiceEvent>>({
      query: (event) => ({
        url: "/service-events",
        method: "PUT",
        body: event,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "ServiceEvents", id},
        {type: "ObjectObjectChangeLogs", id}
      ]
    }),
    deleteServiceEvent: builder.mutation<void, Partial<ServiceEvent>>({
      query: (event) => ({
        url: "/service-events",
        method: "DELETE",
        body: event
      }),
      invalidatesTags: (result, error, {id}) => [{type: "ServiceEvents", id}]
    }),
    activateServiceEvent: builder.mutation<ServiceEvent, Partial<ServiceEventCreateDTO>>({
      query: (event) => ({
        url: "/service-events/activate",
        method: "POST",
        body: event
      }),
      invalidatesTags: [{type: "ServiceEvents", id: "LIST"}]
    }),
  })
});

export const {
  useSearchServiceEventsQuery,
  useGetServiceEventQuery,
  useGetServiceEventBadgeSummaryQuery,
  useGetServiceEventLocationFacetsQuery,
  useGetServiceEventHeatmapGridQuery,
  useLazyGetServiceEventHeatmapGridQuery,
  useCreateServiceEventMutation,
  useUpdateServiceEventMutation,
  useDeleteServiceEventMutation,
  useActivateServiceEventMutation,
} = serviceEventApi;
